import { useMemo } from 'react';
import { TreeSelect } from 'antd';
import { type DocumentCategory } from '@/services/documentService';

/**
 * Component dropdown chọn loại tài liệu dạng cây phân cấp.
 * Cho phép chọn xuống đến thư mục con.
 *
 * Khác với Select thường:
 * - Hiển thị thụt lề theo cấp cha-con
 * - Vẫn cho chọn cả thư mục cha (nếu đó là thư mục có tài liệu)
 * - Search theo tên hoặc mã, tự expand các parent matching
 *
 * Usage:
 *   <CategoryTreeSelect
 *     categories={categories}
 *     value={categoryId}
 *     onChange={setCategoryId}
 *     placeholder="Chọn loại..."
 *   />
 */
interface CategoryTreeSelectProps {
  categories: DocumentCategory[];
  value?: string | null;
  onChange?: (value: string | undefined) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  /**
   * Nếu true: chỉ cho chọn LEAF (thư mục không có con).
   * Mặc định false: cho chọn cả nhánh cha.
   */
  leafOnly?: boolean;
  /** Width override - mặc định 100% */
  width?: number | string;
}

interface TreeNode {
  value: string;
  title: React.ReactNode;
  /** Title dùng khi đã selected (text gọn không có icon/badge) */
  selectedTitle: string;
  children?: TreeNode[];
  /** Disable chọn nếu là branch parent + leafOnly mode */
  selectable?: boolean;
  /** String để search */
  searchText: string;
}

/**
 * Build tree từ flat list categories sorting theo (parent, sortOrder, name).
 * Trả về roots (categories không có parent).
 */
function buildTree(categories: DocumentCategory[], leafOnly: boolean): TreeNode[] {
  const byId = new Map<string, DocumentCategory>();
  categories.forEach(c => byId.set(c.id, c));

  // Group by parent
  const childrenMap = new Map<string | null, DocumentCategory[]>();
  categories.forEach(c => {
    const key = c.parentId ?? null;
    if (!childrenMap.has(key)) childrenMap.set(key, []);
    childrenMap.get(key)!.push(c);
  });

  // Sort theo sortOrder rồi tên
  childrenMap.forEach(arr => {
    arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
  });

  const buildNode = (cat: DocumentCategory): TreeNode => {
    const kids = childrenMap.get(cat.id) ?? [];
    const hasChildren = kids.length > 0;
    return {
      value: cat.id,
      title: (
        <span>
          <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#7d8a98', marginRight: 6 }}>
            {cat.code}
          </span>
          {cat.name}
          {hasChildren && (
            <span style={{ marginLeft: 6, fontSize: 11, color: '#a5b0c0' }}>
              ({kids.length} mục con)
            </span>
          )}
        </span>
      ),
      selectedTitle: `${cat.code} — ${cat.name}`,
      searchText: `${cat.code} ${cat.name}`.toLowerCase(),
      // Nếu leafOnly: parent không cho chọn (chỉ leaf chọn được)
      selectable: leafOnly ? !hasChildren : true,
      children: hasChildren ? kids.map(buildNode) : undefined,
    };
  };

  const roots = childrenMap.get(null) ?? [];
  return roots.map(buildNode);
}

export function CategoryTreeSelect({
  categories, value, onChange, placeholder = 'Chọn loại tài liệu...',
  allowClear = true, disabled, style, leafOnly = false, width = '100%',
}: CategoryTreeSelectProps) {
  const treeData = useMemo(() => buildTree(categories, leafOnly), [categories, leafOnly]);

  return (
    <TreeSelect
      value={value ?? undefined}
      onChange={(v) => onChange?.(v ?? undefined)}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled}
      style={{ width, ...style }}
      treeData={treeData}
      treeDefaultExpandAll
      showSearch
      // Search bằng searchText (lowercase code + name) - case insensitive
      filterTreeNode={(input, node) => {
        const q = input.toLowerCase().trim();
        if (!q) return true;
        return ((node as any).searchText ?? '').includes(q);
      }}
      // Khi đã chọn, hiển thị "code — name" gọn (thay vì JSX phức tạp)
      treeNodeLabelProp="selectedTitle"
      // Khi click vào parent có con: nếu leafOnly = không cho chọn (auto expand)
      // Còn lại cho chọn bình thường
      virtual={false}
      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
    />
  );
}
